import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api from './services/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UnitsProvider } from './contexts/UnitsContext';
import { MobileMenuProvider } from './contexts/MobileMenuContext';
import MobileMenuDrawer from './components/Layout/MobileMenuDrawer';
import MobilePageBar from './components/Layout/MobilePageBar';
import ProtectedRoute from './components/Layout/ProtectedRoute';
import StravaLogin from './components/Auth/StravaLogin';
import Dashboard from './components/Dashboard/Dashboard';
import RaceGoalSetup from './components/Profile/RaceGoalSetup';
import LogWorkoutForm from './components/Workouts/LogWorkoutForm';
import WorkoutList from './components/Workouts/WorkoutList';
import WeeklyPlan from './components/Training/WeeklyPlan';
import AdherenceDashboard from './components/Training/AdherenceDashboard';
import RaceHistoryPage from './components/Training/RaceHistoryPage';
import RaceTimeCalculator from './components/Calculator/RaceTimeCalculator';
import RunningCoach from './components/Coach/RunningCoach';
import BlockedPage from './components/Blocked/BlockedPage';
import WaitlistPage from './components/Waitlist/WaitlistPage';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import RaceSearch from './components/RaceFinder/RaceSearch';
import RaceDetail from './components/RaceFinder/RaceDetail';
import RaceRecommender from './components/RaceFinder/RaceRecommender';
import Navbar from './components/Layout/Navbar';
import OnboardingPage from './components/Onboarding/OnboardingPage';
import OnboardingGate from './components/Onboarding/OnboardingGate';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import ContactUs from './components/Legal/ContactUs';
import './App.css';

// Fires email_link_clicked once per page load when a user lands with ?ref=email.
// Used to measure click-through from the weekly email.
const EmailRefTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ref') === 'email') {
      api.trackEvent('email_link_clicked', { path: location.pathname });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, unitPreference, needsOnboarding } = useAuth();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <UnitsProvider unitPreference={unitPreference}>
    <EmailRefTracker />
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={isAuthenticated
          ? <Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />
          : <StravaLogin />}
      />

      {/* Blocked / waitlist pages */}
      <Route path="/blocked" element={<BlockedPage />} />
      <Route path="/waitlist" element={<WaitlistPage />} />

      {/* Admin routes — not wrapped in ProtectedRoute */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />

      <Route path="/calculator" element={<RaceTimeCalculator />} />
      <Route path="/races" element={<RaceSearch />} />
      <Route path="/races/:slug" element={<RaceDetail />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/contact" element={<ContactUs />} />

      {/* Onboarding — protected but NOT gated (to avoid redirect loop) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Standalone race recommender */}
      <Route
        path="/recommend"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <Navbar />
              <RaceRecommender />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      {/* Protected routes — gated by onboarding */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <Dashboard />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/race-goal"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <RaceGoalSetup />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      {/* Backwards compatibility redirects */}
      <Route path="/profile/race-goal" element={<Navigate to="/race-goal" replace />} />
      <Route path="/profile" element={<Navigate to="/race-goal" replace />} />
      <Route path="/log-workout" element={<Navigate to="/workouts" replace />} />
      <Route path="/adherence" element={<Navigate to="/training-plan" replace />} />

      <Route
        path="/log-workout"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <LogWorkoutForm />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <WorkoutList />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/training-plan"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <WeeklyPlan />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route path="/training-preferences" element={<Navigate to="/race-goal" replace />} />

      <Route
        path="/race-history"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <RaceHistoryPage />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/adherence"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <AdherenceDashboard />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route
        path="/coach"
        element={
          <ProtectedRoute>
            <OnboardingGate>
              <RunningCoach />
            </OnboardingGate>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </UnitsProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <MobileMenuProvider>
          <MobilePageBar />
          <AppRoutes />
          <MobileMenuDrawer />
        </MobileMenuProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
